<template>
  <header class="site-header">
    <div class="header-inner">
      <a :href="resolvedLogoHref" class="logo">
        <img v-if="logoSrc" :src="logoSrc" alt="Logo" class="logo-img" />
        <span v-else class="logo-text">Comake</span>
      </a>

      <!-- 桌面端主菜单 -->
      <nav class="main-menu" aria-label="主导航">
        <ul class="menu-list">
          <li
            v-for="item in parsedMenu"
            :key="item.key"
            class="menu-item"
            :class="{
              'menu-item--has-children': !!item.children?.length,
              'menu-item--active': activeTopKey === item.key
            }"
          >
            <a
              v-if="!item.children?.length"
              :href="item.key"
              class="menu-link"
            >
              {{ item.label }}
            </a>

            <template v-else>
              <span
                class="menu-link menu-link--parent"
                tabindex="0"
                role="button"
                :aria-haspopup="true"
              >
                {{ item.label }}
                <span class="caret" aria-hidden="true">▾</span>
              </span>
              <div
                class="submenu"
                role="menu"
                :class="{ 'submenu--mega': isMegaMenu(item) }"
              >
                <template v-if="isMegaMenu(item)">
                  <div
                    v-for="child in item.children"
                    :key="child.key"
                    class="submenu-col"
                  >
                    <a
                      v-if="!child.children?.length"
                      :href="child.key"
                      class="submenu-col-title submenu-col-title--link"
                      role="menuitem"
                    >
                      {{ child.label }}
                    </a>
                    <template v-else>
                      <div class="submenu-col-title">{{ child.label }}</div>
                      <a
                        v-for="g in child.children"
                        :key="g.key"
                        :href="g.key"
                        class="submenu-item"
                        role="menuitem"
                      >
                        {{ g.label }}
                      </a>
                    </template>
                  </div>
                </template>
                <template v-else>
                  <a
                    v-for="child in item.children"
                    :key="child.key"
                    :href="child.key"
                    class="submenu-item"
                    role="menuitem"
                  >
                    {{ child.label }}
                  </a>
                </template>
              </div>
            </template>
          </li>
        </ul>
      </nav>

      <!-- 右侧：用户区 + 语言切换 -->
      <div class="header-right">
        <div class="user-area">
          <button
            v-if="!parsedIsLogin"
            type="button"
            class="login-btn"
            @click="emit('login-click')"
          >
            {{ loginLabel }}
          </button>

          <div
            v-else
            class="user-menu"
            tabindex="0"
            role="button"
            :aria-label="parsedUser?.name || ''"
            aria-haspopup="true"
          >
            <div class="user-avatar">
              <img
                v-if="parsedUser?.avatar"
                :src="parsedUser.avatar"
                :alt="parsedUser?.name || 'user'"
                class="user-avatar-img"
              />
              <span v-else class="user-avatar-fallback">{{ avatarText }}</span>
            </div>
            <span class="user-name">{{ parsedUser?.name }}</span>
            <span class="caret" aria-hidden="true">▾</span>

            <div class="user-submenu" role="menu">
              <a
                :href="resolvedProfileHref"
                class="user-submenu-item"
                role="menuitem"
              >
                {{ profileLabel }}
              </a>
              <button
                type="button"
                class="user-submenu-item user-submenu-item--btn"
                role="menuitem"
                @click="emit('logout-click')"
              >
                {{ logoutLabel }}
              </button>
              <button
                type="button"
                class="user-submenu-item user-submenu-item--btn user-submenu-item--danger"
                role="menuitem"
                @click="openDeactivate"
              >
                {{ deactivateLabel }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="parsedLangs.length > 1" class="lang-switch" role="group" aria-label="语言切换">
          <button
            v-for="opt in parsedLangs"
            :key="opt.value"
            type="button"
            class="lang-btn"
            :class="{ 'lang-btn--active': currentLang === opt.value }"
            @click="onLangChange(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>

      <!-- 移动端汉堡按钮 -->
      <button
        type="button"
        class="mobile-toggle"
        :aria-label="mobileOpen ? '关闭菜单' : '打开菜单'"
        :aria-expanded="mobileOpen"
        @click="toggleMobile"
      >
        <!-- Menu icon -->
        <svg v-if="!mobileOpen" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="4" x2="20" y1="12" y2="12" /><line x1="4" x2="20" y1="6" y2="6" /><line x1="4" x2="20" y1="18" y2="18" />
        </svg>
        <!-- X icon -->
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 6 6 18" /><path d="m6 6 12 12" />
        </svg>
      </button>
    </div>

    <!-- 移动端遮罩 -->
    <Transition name="drawer-fade">
      <div v-if="mobileOpen" class="mobile-mask" @click="closeMobile" />
    </Transition>

    <!-- 移动端抽屉 -->
    <Transition name="drawer-slide">
      <aside v-if="mobileOpen" class="mobile-drawer" role="dialog" aria-modal="true">
        <div class="mobile-user">
          <template v-if="parsedIsLogin">
            <div class="mobile-user-avatar">
              <img v-if="parsedUser?.avatar" :src="parsedUser.avatar" :alt="parsedUser?.name || 'user'" class="mobile-avatar-img" />
              <span v-else class="mobile-avatar-fallback">{{ avatarText }}</span>
            </div>
            <div class="mobile-user-info">
              <div class="mobile-user-name">{{ parsedUser?.name }}</div>
              <div class="mobile-user-meta">{{ parsedUser?.account }}</div>
            </div>
          </template>
          <template v-else>
            <div class="mobile-user-tip">{{ pleaseLoginLabel }}</div>
            <button type="button" class="login-btn" @click="onMobileLogin">
              {{ loginLabel }}
            </button>
          </template>
        </div>

        <nav class="mobile-nav" aria-label="移动端导航">
          <template v-for="item in parsedMenu" :key="item.key">
            <a
              v-if="!item.children?.length"
              :href="item.key"
              class="mobile-link"
              @click="closeMobile"
            >
              {{ item.label }}
            </a>

            <div v-else class="mobile-group" :class="{ open: mobileExpanded[item.key] }">
              <button
                type="button"
                class="mobile-link mobile-group-head"
                @click="toggleGroup(item.key)"
              >
                <span>{{ item.label }}</span>
                <svg class="mobile-group-arrow" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
              <div v-show="mobileExpanded[item.key]" class="mobile-sub">
                <template v-for="child in item.children" :key="child.key">
                  <a
                    v-if="!child.children?.length"
                    :href="child.key"
                    class="mobile-sub-link"
                    @click="closeMobile"
                  >
                    {{ child.label }}
                  </a>
                  <div
                    v-else
                    class="mobile-subgroup"
                    :class="{ open: mobileExpanded[child.key] }"
                  >
                    <button
                      type="button"
                      class="mobile-sub-link mobile-subgroup-head"
                      @click="toggleGroup(child.key)"
                    >
                      <span>{{ child.label }}</span>
                      <svg class="mobile-group-arrow" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </button>
                    <div v-show="mobileExpanded[child.key]" class="mobile-sub-sub">
                      <a
                        v-for="g in child.children"
                        :key="g.key"
                        :href="g.key"
                        class="mobile-sub-sub-link"
                        @click="closeMobile"
                      >
                        {{ g.label }}
                      </a>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </template>

          <a
            v-if="parsedIsLogin"
            :href="resolvedProfileHref"
            class="mobile-link"
            @click="closeMobile"
          >
            {{ profileLabel }}
          </a>
        </nav>

        <div class="mobile-bottom">
          <div v-if="parsedLangs.length > 1" class="lang-switch" role="group" aria-label="语言切换">
            <button
              v-for="opt in parsedLangs"
              :key="opt.value"
              type="button"
              class="lang-btn"
              :class="{ 'lang-btn--active': currentLang === opt.value }"
              @click="onLangChange(opt.value)"
            >
              {{ opt.label }}
            </button>
          </div>
          <button
            v-if="parsedIsLogin"
            type="button"
            class="logout-btn"
            @click="onMobileLogout"
          >
            {{ logoutLabel }}
          </button>
          <button
            v-if="parsedIsLogin"
            type="button"
            class="logout-btn logout-btn--danger"
            @click="openDeactivate"
          >
            {{ deactivateLabel }}
          </button>
        </div>
      </aside>
    </Transition>
  </header>

  <!-- ===== 注销用户确认弹窗 ===== -->
  <Transition name="drawer-fade">
    <div
      v-if="deactivateOpen"
      class="deactivate-mask"
      @click.self="closeDeactivate"
    >
      <div class="deactivate-dialog" role="dialog" aria-modal="true">
        <div class="deactivate-title">{{ deactivateLabel }}</div>
        <p class="deactivate-desc">{{ deactivateConfirmText }}</p>
        <p v-if="deactivateError" class="deactivate-error">{{ deactivateError }}</p>
        <div class="deactivate-actions">
          <button
            type="button"
            class="deactivate-btn deactivate-btn--cancel"
            :disabled="deactivating"
            @click="closeDeactivate"
          >
            {{ deactivateCancelLabel }}
          </button>
          <button
            type="button"
            class="deactivate-btn deactivate-btn--confirm"
            :disabled="deactivating"
            @click="confirmDeactivate"
          >
            <span v-if="deactivating" class="deactivate-spinner" aria-hidden="true" />
            {{ deactivateConfirmLabel }}
          </button>
        </div>
      </div>
    </div>
  </Transition>

  <!-- ===== 右侧悬浮按钮组：问题反馈在上、客服在下（客服面板挂到 document.body） ===== -->
  <div class="fab-stack">
    <a
      class="cs-fab cs-fab--feedback"
      :href="FEEDBACK_URL"
      target="_blank"
      rel="noopener noreferrer"
      :aria-label="fbLabel"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
        <path d="M14 3v6h6" />
        <path d="M8 13h8" />
        <path d="M8 17h5" />
      </svg>
      <span>{{ fbLabel }}</span>
    </a>

    <button
      class="cs-fab"
      :class="{ 'cs-fab--active': csOpen }"
      :aria-label="csLabel"
      @click="toggleCs"
    >
      <svg v-if="!csOpen" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
      <svg v-else xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M18 6 6 18" /><path d="m6 6 12 12" />
      </svg>
      <span>{{ csLabel }}</span>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import type { NavConfig, NavItem, NavUser, NavLangOption } from './types/nav'
import { buildDefaultMenuConfig } from './defaultMenu'
import {
  applySiteLocaleSwitch,
  buildMenuPath,
  CS_API_URL,
  DEFAULT_LOGO_SRC,
  DEFAULT_SITE_PREFIX,
  FEEDBACK_URL,
  isLocaleLabelOnlyOrigin,
  linkKeyForActiveMatch,
  normalizePrefix
} from './menuUrl'

const props = withDefaults(defineProps<{
  /**
   * 站点根路径前缀。不传时使用 `DEFAULT_SITE_PREFIX`；
   * 传 `/` 表示当前源站相对路径。
   */
  prefix?: string
  /** 自定义菜单 JSON；不传则使用内置默认菜单（与 SSG 站一致） */
  menuConfig?: string
  /** Logo 图片 URL；不传时使用 `DEFAULT_LOGO_SRC` */
  logoSrc?: string
  /** Logo 点击跳转地址；默认取 prefix 或 `/` */
  logoHref?: string
  /** 是否已登录，布尔或字符串 "true"/"false" */
  isLogin?: boolean | string
  /** 当前用户 JSON 字符串，结构见 NavUser */
  user?: string
  /** 当前语言代码，如 'zh' / 'en' */
  lang?: string
  /** 语言选项 JSON 字符串，结构见 NavLangOption[]；少于 2 个时不显示切换器 */
  langs?: string
  /** 个人中心页地址，默认 '/account' */
  profileHref?: string
  /** 登录按钮文字 */
  loginLabel?: string
  /** 退出按钮文字 */
  logoutLabel?: string
  /** 个人中心菜单文字 */
  profileLabel?: string
  /** 未登录提示文字 */
  pleaseLoginLabel?: string
  /** 客服按钮文字，默认"官方客服" */
  csLabel?: string
  /** 问题反馈按钮文字，默认"问题反馈" */
  fbLabel?: string
  /**
   * CAS 服务基地址，用于调用「注销用户」接口（POST `${casBaseUrl}/api/login/ZhuXiao`）。
   * 不传时使用默认生产地址。
   */
  casBaseUrl?: string
  /** 注销菜单文字；不传时按当前语言取默认值（注销用户 / Deactivate account） */
  deactivateLabel?: string
  /** 注销确认弹窗的提示文案；不传时按当前语言取默认值 */
  deactivateConfirmText?: string
  /** 注销确认按钮文字；不传时按当前语言取默认值（确认注销 / Confirm） */
  deactivateConfirmLabel?: string
  /** 注销取消按钮文字；不传时按当前语言取默认值（取消 / Cancel） */
  deactivateCancelLabel?: string
  /**
   * 为 true 时语言切换只抛 `lang-change`，不自行 `location.assign` / 写 CAS。
   * 供 Nuxt 等宿主用 SPA `setLocale` 统一处理，避免「header 硬跳 + 宿主再跳」两次刷新。
   */
  externalLocaleSwitch?: boolean | string
}>(), {
  prefix: DEFAULT_SITE_PREFIX,
  logoSrc: DEFAULT_LOGO_SRC,
  isLogin: false,
  lang: 'zh',
  langs: '[{"label":"中文","value":"zh"},{"label":"EN","value":"en"}]',
  loginLabel: '登录',
  logoutLabel: '退出',
  profileLabel: '个人中心',
  pleaseLoginLabel: '请先登录',
  csLabel: '官方客服',
  fbLabel: '问题反馈',
  casBaseUrl: 'https://cas.comake.online',
  deactivateLabel: '',
  deactivateConfirmText: '',
  deactivateConfirmLabel: '',
  deactivateCancelLabel: '',
  externalLocaleSwitch: false,
})

const emit = defineEmits<{
  /** 点击登录按钮 */
  'login-click': []
  /** 点击退出按钮 */
  'logout-click': []
  /** 切换语言，payload 为语言代码 */
  'lang-change': [lang: string]
  /** 注销成功：宿主应清空本地登录态并跳转 */
  'deactivate-success': []
  /** 注销失败：payload 为错误信息，供宿主上报/提示 */
  'deactivate-error': [message: string]
}>()

/* ============================= 数据解析 ============================= */

const hasCustomMenu = computed(() => {
  const raw = props.menuConfig?.trim()
  return !!raw && raw !== '[]'
})

/** 论坛等场景：链接路径固定，仅 label 随语言切换 */
const menuPathLang = computed(() =>
  isLocaleLabelOnlyOrigin() ? 'zh' : currentLang.value
)

const parsedMenu = computed<NavConfig>(() => {
  if (hasCustomMenu.value) {
    try { return JSON.parse(props.menuConfig || '[]') } catch { return [] }
  }
  return buildDefaultMenuConfig(
    currentLang.value,
    props.prefix,
    menuPathLang.value
  )
})

const resolvedLogoHref = computed(() => {
  if (props.logoHref) return props.logoHref
  const base = normalizePrefix(props.prefix)
  return base || '/'
})

const resolvedProfileHref = computed(() => {
  if (props.profileHref) return props.profileHref
  return buildMenuPath('/account', currentLang.value, props.prefix)
})

const parsedIsLogin = computed(() => {
  if (typeof props.isLogin === 'string') return props.isLogin === 'true'
  return !!props.isLogin
})

const parsedUser = computed<NavUser | null>(() => {
  if (!props.user) return null
  try { return JSON.parse(props.user) } catch { return null }
})

const parsedLangs = computed<NavLangOption[]>(() => {
  try { return JSON.parse(props.langs || '[]') } catch { return [] }
})

const localeLang = ref(props.lang || 'zh')
watch(() => props.lang, (v) => {
  if (v) localeLang.value = v
})
const currentLang = computed(() => localeLang.value)

const avatarText = computed(() => {
  const name = (parsedUser.value?.name || parsedUser.value?.account || '').trim()
  if (!name) return 'U'
  return (Array.from(name)[0] || 'U').toUpperCase()
})

// 若外部传入的 prop 是已知的中/英默认值（或未传），则随 lang 自动切换；
// 若是真正自定义的文案则原样使用。
const loginLabel = computed(() => {
  const v = props.loginLabel
  return !v || v === '登录' || v === 'Sign in'
    ? (currentLang.value === 'en' ? 'Sign in' : '登录')
    : v
})
const logoutLabel = computed(() => {
  const v = props.logoutLabel
  return !v || v === '退出' || v === 'Sign out'
    ? (currentLang.value === 'en' ? 'Sign out' : '退出')
    : v
})
const profileLabel = computed(() => {
  const v = props.profileLabel
  return !v || v === '个人中心' || v === 'My account'
    ? (currentLang.value === 'en' ? 'My account' : '个人中心')
    : v
})
const pleaseLoginLabel = computed(() => {
  const v = props.pleaseLoginLabel
  return !v || v === '请先登录' || v === 'Please sign in first'
    ? (currentLang.value === 'en' ? 'Please sign in first' : '请先登录')
    : v
})
const csLabel = computed(() => {
  const v = props.csLabel
  return !v || v === '客服' || v === '官方客服' || v === 'Support'
    ? (currentLang.value === 'en' ? 'Support' : '官方客服')
    : v
})
const fbLabel = computed(() => {
  const v = props.fbLabel
  return !v || v === '问题反馈' || v === 'Feedback'
    ? (currentLang.value === 'en' ? 'Feedback' : '问题反馈')
    : v
})

const isEn = computed(() => currentLang.value === 'en')
const deactivateLabel = computed(
  () => props.deactivateLabel || (isEn.value ? 'Deactivate account' : '注销用户')
)
const deactivateConfirmText = computed(
  () =>
    props.deactivateConfirmText ||
    (isEn.value
      ? 'Your account will be permanently deleted and cannot be recovered. Continue?'
      : '注销后您的账号将被永久删除且无法恢复，确定要继续吗？')
)
const deactivateConfirmLabel = computed(
  () => props.deactivateConfirmLabel || (isEn.value ? 'Confirm' : '确认注销')
)
const deactivateCancelLabel = computed(
  () => props.deactivateCancelLabel || (isEn.value ? 'Cancel' : '取消')
)

/* ============================= 注销用户 ============================= */

const deactivateOpen = ref(false)
const deactivating = ref(false)
const deactivateError = ref('')

const openDeactivate = () => {
  deactivateError.value = ''
  deactivateOpen.value = true
}

const closeDeactivate = () => {
  if (deactivating.value) return
  deactivateOpen.value = false
}

/**
 * 调用 CAS「注销用户」接口（POST `${casBaseUrl}/api/login/ZhuXiao`）。
 * 入参：login_ticket（tb_userlogin 登录凭证）、uuid（tb_userlogin 用户唯一标识）。
 * 业务逻辑内聚在组件内，任何引用本顶部栏的站点都复用同一套接口与流程。
 */
const confirmDeactivate = async () => {
  if (deactivating.value) return
  const u = parsedUser.value
  const login_ticket = u?.login_ticket
  const uuid = u?.uuid || u?.account
  if (!login_ticket || !uuid) {
    deactivateError.value = isEn.value
      ? 'Missing login credentials, please sign in again.'
      : '缺少登录凭证，请重新登录后再试。'
    return
  }

  deactivating.value = true
  deactivateError.value = ''
  try {
    const base = (props.casBaseUrl || 'https://cas.comake.online').replace(/\/$/, '')
    const resp = await fetch(`${base}/api/login/ZhuXiao`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ login_ticket, uuid })
    })
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
    // CAS 约定：result === 0 为成功；部分接口直接 200 无包装时也视为成功
    const data = await resp.json().catch(() => ({} as Record<string, unknown>))
    const result = (data as { result?: unknown })?.result
    if (typeof result === 'number' && result !== 0) {
      const msg = (data as { msg?: string })?.msg || 'deactivate failed'
      throw new Error(msg)
    }
    deactivateOpen.value = false
    closeMobile()
    emit('deactivate-success')
  } catch (e) {
    deactivateError.value = isEn.value
      ? 'Account deactivation failed, please try again later.'
      : '注销失败，请稍后重试。'
    emit('deactivate-error', String((e as Error)?.message || e))
  } finally {
    deactivating.value = false
  }
}

/**
 * 读取 cookie 中的 comake_login_ticket。
 */
function readLoginTicketFromCookie(): string {
  if (typeof document === 'undefined') return ''
  const m = document.cookie.match(/(?:^|;\s*)comake_login_ticket=([^;]+)/)
  return m ? decodeURIComponent(m[1]) : ''
}

/**
 * 登录态下切换语言时，同步到 CAS：POST `${casBaseUrl}/api/login/setLanguage`。
 * 入参：language（zh/en）、uuid（用户唯一标识）、ticket（cookie comake_login_ticket）。
 * 失败不影响前端语言切换，仅做静默上报。
 */
const syncLanguageToCas = async (lang: string) => {
  if (!parsedIsLogin.value) return
  const uuid = parsedUser.value?.uuid || parsedUser.value?.account
  const ticket = readLoginTicketFromCookie()
  if (!uuid || !ticket) return
  try {
    const base = (props.casBaseUrl || 'https://cas.comake.online').replace(/\/$/, '')
    await fetch(`${base}/api/login/setLanguage`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ language: lang, uuid, ticket }),
      keepalive: true
    })
  } catch {
    /* 静默失败，不影响前端语言切换 */
  }
}

const parsedExternalLocaleSwitch = computed(() => {
  if (typeof props.externalLocaleSwitch === 'string') {
    return props.externalLocaleSwitch === 'true'
  }
  return !!props.externalLocaleSwitch
})

/** 论坛仅切换菜单文案；主站则跳转 /en 路径，并抛出 lang-change */
const onLangChange = (lang: string) => {
  if (lang === currentLang.value) return
  // 宿主接管：只发事件，由 Nuxt setSiteLocale 写 CAS + 单次 SPA 切语言
  if (parsedExternalLocaleSwitch.value) {
    emit('lang-change', lang)
    return
  }
  syncLanguageToCas(lang)
  if (isLocaleLabelOnlyOrigin()) {
    localeLang.value = lang
  } else {
    applySiteLocaleSwitch(lang, props.prefix)
  }
  emit('lang-change', lang)
}

/* ============================= 激活状态 ============================= */

const currentPath = ref(typeof window !== 'undefined' ? window.location.pathname : '/')

const onPopState = () => { currentPath.value = window.location.pathname }

onMounted(() => window.addEventListener('popstate', onPopState))

const collectLeaves = (node: NavItem): NavItem[] => {
  if (!node.children?.length) return [node]
  return node.children.flatMap(collectLeaves)
}

const isMegaMenu = (item: NavItem) =>
  !!item.children?.some((c) => !!c.children?.length)

const activeTopKey = computed(() => {
  const path = currentPath.value
  for (const top of parsedMenu.value) {
    if (
      top.children?.length &&
      collectLeaves(top).some((c) => {
        const p = linkKeyForActiveMatch(c.key, props.prefix)
        return p && p !== '/' && path.startsWith(p)
      })
    ) {
      return top.key
    }
    const topPath = linkKeyForActiveMatch(top.key, props.prefix)
    if (topPath !== '/' && path.startsWith(topPath)) return top.key
  }
  return ''
})

/* ============================= 移动端抽屉 ============================= */

const mobileOpen = ref(false)
const mobileExpanded = reactive<Record<string, boolean>>({})

const toggleMobile = () => { mobileOpen.value = !mobileOpen.value }
const closeMobile = () => { mobileOpen.value = false }
const toggleGroup = (key: string) => { mobileExpanded[key] = !mobileExpanded[key] }

const onMobileLogin = () => {
  closeMobile()
  emit('login-click')
}
const onMobileLogout = () => {
  closeMobile()
  emit('logout-click')
}

watch(mobileOpen, (open) => {
  if (typeof document === 'undefined') return
  document.body.style.overflow = open ? 'hidden' : ''
})

watch(activeTopKey, (key) => {
  if (key && !(key in mobileExpanded)) mobileExpanded[key] = true
}, { immediate: true })

/* ============================= 客服 Widget ============================= */
/*
 * 面板容器创建在 document.body 而非 Shadow DOM 内：
 * Widget 内部使用 document.querySelector / document.createElement，
 * 必须挂在普通 DOM 树才能正常工作。
 */

const csOpen = ref(false)
const csScriptLoaded = ref(false)
const csWidgetMounted = ref(false)

const CS_WIDGET_SRC = 'https://dev.comake.online/custom_service/widget/feishu-support-widget.iife.js'

let csPanelEl: HTMLDivElement | null = null
let csInnerEl: HTMLDivElement | null = null

/** 在 body 中创建面板 DOM（仅执行一次） */
function ensureCsPanel() {
  if (csPanelEl) return

  csPanelEl = document.createElement('div')
  csPanelEl.setAttribute('role', 'dialog')
  csPanelEl.setAttribute('aria-label', csLabel.value)
  applyCsPanelStyle(csPanelEl)
  csPanelEl.style.display = 'none'
  csPanelEl.style.opacity = '0'
  csPanelEl.style.transform = 'translateY(16px) scale(0.97)'

  csInnerEl = document.createElement('div')
  csInnerEl.style.cssText = 'width:100%;height:100%;display:block;'
  csPanelEl.appendChild(csInnerEl)
  document.body.appendChild(csPanelEl)
}

function applyCsPanelStyle(el: HTMLDivElement) {
  const mobile = window.innerWidth <= 480
  Object.assign(el.style, {
    position: 'fixed',
    right: mobile ? '0' : '24px',
    bottom: mobile ? '0' : '140px',
    left: mobile ? '0' : 'auto',
    top: mobile ? '0' : 'auto',
    zIndex: '999998',
    width: mobile ? '100%' : '380px',
    height: mobile ? '100%' : '540px',
    background: '#fff',
    borderRadius: mobile ? '0' : '16px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.16), 0 2px 8px rgba(0,0,0,0.08)',
    overflow: 'hidden',
    flexDirection: 'column',
    transition: 'opacity 0.2s ease, transform 0.25s ease',
    transformOrigin: 'bottom right',
  })
}

function showCsPanel() {
  if (!csPanelEl) return
  applyCsPanelStyle(csPanelEl)
  csPanelEl.style.display = 'flex'
  requestAnimationFrame(() => {
    if (!csPanelEl) return
    csPanelEl.style.opacity = '1'
    csPanelEl.style.transform = 'translateY(0) scale(1)'
  })
}

function hideCsPanel() {
  if (!csPanelEl) return
  csPanelEl.style.opacity = '0'
  csPanelEl.style.transform = 'translateY(16px) scale(0.97)'
  setTimeout(() => { if (csPanelEl) csPanelEl.style.display = 'none' }, 200)
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return }
    const el = document.createElement('script')
    el.src = src
    el.onload = () => resolve()
    el.onerror = reject
    document.head.appendChild(el)
  })
}

async function mountCsWidget() {
  if (csWidgetMounted.value) return
  ensureCsPanel()
  if (!csScriptLoaded.value) {
    await loadScript(CS_WIDGET_SRC)
    csScriptLoaded.value = true
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = (window as any).ComakeFeishuSupportWidget
  if (w && csInnerEl) {
    w.mount(csInnerEl, {
      apiBaseUrl: CS_API_URL,
      layoutMode: 'fullscreen',
      height: '100%'
    })
    csWidgetMounted.value = true
  }
}

async function toggleCs() {
  csOpen.value = !csOpen.value
  if (csOpen.value) {
    await mountCsWidget()
    showCsPanel()
  } else {
    hideCsPanel()
  }
}

onUnmounted(() => {
  window.removeEventListener('popstate', onPopState)
  if (csPanelEl) {
    document.body.removeChild(csPanelEl)
    csPanelEl = null
    csInnerEl = null
  }
})
</script>

<style>
/*
 * Shadow DOM 内的 CSS 变量优先读取宿主页面透传的值，
 * 未定义时使用括号内的默认值，保证在任何环境下都有合理外观。
 */
:host {
  --header-h: 64px;
  --content-max-w: 1200px;
  --brand: #1677ff;
  --text-1: rgba(0, 0, 0, 0.88);
  --text-2: rgba(0, 0, 0, 0.65);
  --text-3: rgba(0, 0, 0, 0.45);
  --border-1: #e5e7eb;
  display: block;
  font-family: 'Microsoft YaHei', '微软雅黑', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  font-size: 14px;
  box-sizing: border-box;
}

*, *::before, *::after {
  box-sizing: inherit;
}

.site-header {
  position: sticky;
  top: 0;
  z-index: 50;
  background: #fff;
  height: var(--header-h);
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.06);
}
.header-inner {
  max-width: var(--content-max-w);
  margin: 0 auto;
  height: var(--header-h);
  padding: 0 16px;
  display: flex;
  align-items: center;
  gap: 24px;
}
.logo {
  display: flex;
  align-items: center;
  text-decoration: none;
  color: inherit;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
}
.logo:focus,
.logo:focus-visible {
  outline: none;
  box-shadow: none;
}
.logo-img {
  height: 18px;
  width: auto;
  display: block;
  object-fit: contain;
  border: none;
  outline: none;
  background: transparent;
  box-shadow: none;
}
.logo-text {
  font-size: 18px;
  font-weight: 700;
  color: var(--brand);
}

/* ===== 主菜单 ===== */
.main-menu {
  flex: 1;
  height: 100%;
}
.menu-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: stretch;
  height: 100%;
  gap: 4px;
}
.menu-item {
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
}
.menu-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 100%;
  padding: 0 16px;
  font-size: 14px;
  color: var(--text-1);
  text-decoration: none;
  white-space: nowrap;
  border-bottom: 2px solid transparent;
  transition: color 0.18s ease, border-color 0.18s ease;
}
.menu-link:hover {
  color: var(--brand);
}
.menu-link--parent {
  cursor: default;
  user-select: none;
}
.menu-item--active > .menu-link {
  color: var(--brand);
  border-bottom-color: var(--brand);
}
.caret {
  font-size: 10px;
  line-height: 1;
  transition: transform 0.2s ease;
}
.menu-item--has-children:hover .caret {
  transform: rotate(180deg);
}

/* ===== 二级浮层 ===== */
.submenu {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 160px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08), 0 3px 6px rgba(0, 0, 0, 0.04);
  padding: 6px;
  display: flex;
  flex-direction: column;
  opacity: 0;
  visibility: hidden;
  transform: translateY(6px);
  transition: opacity 0.18s ease, transform 0.18s ease, visibility 0.18s ease;
  z-index: 1100;
}
.menu-item--has-children::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  height: 8px;
}
.menu-item--has-children:hover .submenu,
.menu-item--has-children:focus-within .submenu {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}
.submenu-item {
  display: block;
  padding: 8px 14px;
  font-size: 14px;
  color: var(--text-1);
  text-decoration: none;
  border-radius: 6px;
  white-space: nowrap;
  line-height: 1.4;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.submenu-item:hover {
  background: rgba(22, 119, 255, 0.08);
  color: var(--brand);
}

/* ===== 巨菜单 ===== */
.submenu--mega {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 8px;
  padding: 12px;
  min-width: 360px;
}
.submenu-col {
  flex: 1 1 0;
  min-width: 120px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.submenu-col + .submenu-col {
  border-left: 1px solid var(--border-1);
  padding-left: 8px;
  margin-left: 4px;
}
.submenu-col-title {
  padding: 6px 10px 8px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-3);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.submenu-col-title--link {
  display: block;
  font-size: 14px;
  color: var(--text-1);
  text-transform: none;
  letter-spacing: 0;
  font-weight: 500;
  border-radius: 6px;
  text-decoration: none;
  padding: 8px 12px;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.submenu-col-title--link:hover {
  background: rgba(22, 119, 255, 0.08);
  color: var(--brand);
}

/* ===== 右侧区域 ===== */
.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}
.user-area {
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
}
.login-btn {
  appearance: none;
  border: 1px solid var(--brand);
  background: transparent;
  color: var(--brand);
  padding: 4px 14px;
  font-size: 13px;
  line-height: 1.6;
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.login-btn:hover {
  background: var(--brand);
  color: #fff;
}

/* ===== 用户菜单 ===== */
.user-menu {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 100%;
  padding: 0 4px;
  cursor: default;
  user-select: none;
  outline: none;
}
.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(22, 119, 255, 0.12);
  flex-shrink: 0;
}
.user-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.user-avatar-fallback {
  font-size: 13px;
  font-weight: 600;
  color: var(--brand);
}
.user-name {
  font-size: 14px;
  color: var(--text-1);
  max-width: 96px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.user-menu .caret {
  font-size: 10px;
  line-height: 1;
  color: var(--text-2);
  transition: transform 0.2s ease;
}
.user-menu:hover .caret,
.user-menu:focus-within .caret {
  transform: rotate(180deg);
}
.user-submenu {
  position: absolute;
  top: 100%;
  right: 0;
  min-width: 140px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08), 0 3px 6px rgba(0, 0, 0, 0.04);
  padding: 6px;
  display: flex;
  flex-direction: column;
  opacity: 0;
  visibility: hidden;
  transform: translateY(6px);
  transition: opacity 0.18s ease, transform 0.18s ease, visibility 0.18s ease;
  z-index: 1100;
}
.user-menu::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  height: 8px;
}
.user-menu:hover .user-submenu,
.user-menu:focus-within .user-submenu {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}
.user-submenu-item {
  display: block;
  padding: 8px 14px;
  font-size: 14px;
  color: var(--text-1);
  text-decoration: none;
  border-radius: 6px;
  white-space: nowrap;
  line-height: 1.4;
  text-align: left;
  transition: background-color 0.15s ease, color 0.15s ease;
}
.user-submenu-item:hover {
  background: rgba(22, 119, 255, 0.08);
  color: var(--brand);
}
.user-submenu-item--btn {
  appearance: none;
  border: 0;
  background: transparent;
  width: 100%;
  cursor: pointer;
  font-family: inherit;
}
.user-submenu-item--danger {
  color: #cf1322;
}
.user-submenu-item--danger:hover {
  background: rgba(207, 19, 34, 0.08);
  color: #cf1322;
}

/* ===== 语言切换 ===== */
.lang-switch {
  display: inline-flex;
  background: #f0f2f5;
  border-radius: 8px;
  padding: 2px;
}
.lang-btn {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px 12px;
  font-size: 13px;
  line-height: 1.4;
  color: var(--text-2);
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  font-family: inherit;
}
.lang-btn:hover {
  color: var(--text-1);
}
.lang-btn--active {
  background: #fff;
  color: var(--text-1);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

/* ===== 汉堡按钮 ===== */
.mobile-toggle {
  display: none;
  appearance: none;
  border: 0;
  background: transparent;
  color: var(--text-1);
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  margin-left: auto;
}
.mobile-toggle:hover {
  background: #f5f7fb;
}

/* ===== 移动端遮罩 / 抽屉 ===== */
.mobile-mask {
  position: fixed;
  inset: 0;
  top: var(--header-h);
  background: rgba(0, 0, 0, 0.35);
  z-index: 45;
}
.mobile-drawer {
  position: fixed;
  top: var(--header-h);
  right: 0;
  bottom: 0;
  width: min(82vw, 320px);
  background: #fff;
  z-index: 46;
  display: flex;
  flex-direction: column;
  box-shadow: -6px 0 16px rgba(0, 0, 0, 0.08);
  overflow-y: auto;
}
.mobile-user {
  padding: 16px;
  border-bottom: 1px solid var(--border-1);
  display: flex;
  align-items: center;
  gap: 12px;
}
.mobile-user-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(22, 119, 255, 0.12);
  flex-shrink: 0;
}
.mobile-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.mobile-avatar-fallback {
  font-size: 16px;
  font-weight: 600;
  color: var(--brand);
}
.mobile-user-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.mobile-user-name {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-1);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mobile-user-meta {
  font-size: 12px;
  color: var(--text-3);
}
.mobile-user-tip {
  flex: 1;
  font-size: 14px;
  color: var(--text-2);
}
.mobile-nav {
  flex: 1;
  padding: 8px 0;
}
.mobile-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  font-size: 15px;
  color: var(--text-1);
  text-decoration: none;
  border: 0;
  background: transparent;
  width: 100%;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
}
.mobile-link:hover,
.mobile-link:active {
  background: #f5f7fb;
}
.mobile-group.open .mobile-group-arrow {
  transform: rotate(180deg);
}
.mobile-group-arrow {
  transition: transform 0.2s;
  color: var(--text-3);
  flex-shrink: 0;
}
.mobile-sub {
  background: #f8f9fc;
  padding: 4px 0;
}
.mobile-sub-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px 10px 36px;
  font-size: 14px;
  color: var(--text-2);
  text-decoration: none;
  border: 0;
  background: transparent;
  width: 100%;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
}
.mobile-sub-link:hover {
  background: #eef2f7;
  color: var(--brand);
}
.mobile-subgroup-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  border: 0;
  background: transparent;
  font-family: inherit;
  cursor: pointer;
  font-weight: 500;
  color: var(--text-1);
}
.mobile-subgroup.open .mobile-group-arrow {
  transform: rotate(180deg);
}
.mobile-sub-sub {
  background: #eef2f7;
  padding: 4px 0;
}
.mobile-sub-sub-link {
  display: block;
  padding: 8px 16px 8px 56px;
  font-size: 13px;
  color: var(--text-2);
  text-decoration: none;
}
.mobile-sub-sub-link:hover {
  background: #e2e8f0;
  color: var(--brand);
}
.mobile-bottom {
  border-top: 1px solid var(--border-1);
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.logout-btn {
  appearance: none;
  border: 1px solid var(--border-1);
  background: transparent;
  color: var(--text-2);
  padding: 4px 12px;
  font-size: 13px;
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
}
.logout-btn:hover {
  border-color: var(--brand);
  color: var(--brand);
}
.logout-btn--danger {
  color: #cf1322;
}
.logout-btn--danger:hover {
  border-color: #cf1322;
  color: #cf1322;
}

/* ===== 抽屉过渡 ===== */
.drawer-fade-enter-active,
.drawer-fade-leave-active {
  transition: opacity 0.2s ease;
}
.drawer-fade-enter-from,
.drawer-fade-leave-to {
  opacity: 0;
}
.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: transform 0.25s ease;
}
.drawer-slide-enter-from,
.drawer-slide-leave-to {
  transform: translateX(100%);
}

/* ===== 响应式 ===== */
@media (max-width: 768px) {
  .header-inner {
    padding: 0 12px;
    gap: 12px;
  }
  .logo-img {
    height: 28px;
  }
  .main-menu,
  .header-right {
    display: none;
  }
  .mobile-toggle {
    display: inline-flex;
  }
}

/* ===== 右侧悬浮按钮组：问题反馈 + 客服 ===== */
.fab-stack {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 999;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}
.cs-fab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  background: var(--brand, #1677ff);
  color: #fff;
  border: none;
  border-radius: 24px;
  font-size: 14px;
  font-family: inherit;
  font-weight: 500;
  line-height: 1;
  text-decoration: none;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(22, 119, 255, 0.45);
  transition: background 0.2s, transform 0.2s, box-shadow 0.2s;
  user-select: none;
}
.cs-fab:hover {
  background: #3d8bfd;
  box-shadow: 0 6px 16px rgba(22, 119, 255, 0.5);
  transform: translateY(-1px);
}
.cs-fab--active {
  background: #595959;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}
.cs-fab--active:hover {
  background: #434343;
  transform: translateY(-1px);
}

/* ===== 移动端：FAB 位置微调 ===== */
@media (max-width: 480px) {
  .fab-stack {
    right: 16px;
    bottom: 16px;
  }
}

/* ===== 注销确认弹窗 ===== */
.deactivate-mask {
  position: fixed;
  inset: 0;
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.45);
}
.deactivate-dialog {
  width: 100%;
  max-width: 400px;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}
.deactivate-title {
  font-size: 17px;
  font-weight: 600;
  color: var(--text-1);
  margin-bottom: 12px;
}
.deactivate-desc {
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-2);
  margin: 0 0 8px;
}
.deactivate-error {
  font-size: 13px;
  line-height: 1.5;
  color: #cf1322;
  margin: 0 0 8px;
}
.deactivate-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}
.deactivate-btn {
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 18px;
  font-size: 14px;
  line-height: 1.5;
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
}
.deactivate-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.deactivate-btn--cancel {
  border: 1px solid var(--border-1);
  background: #fff;
  color: var(--text-2);
}
.deactivate-btn--cancel:hover:not(:disabled) {
  border-color: var(--text-3);
  color: var(--text-1);
}
.deactivate-btn--confirm {
  border: 1px solid #cf1322;
  background: #cf1322;
  color: #fff;
}
.deactivate-btn--confirm:hover:not(:disabled) {
  background: #a8071a;
  border-color: #a8071a;
}
.deactivate-spinner {
  width: 13px;
  height: 13px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-top-color: #fff;
  border-radius: 50%;
  animation: deactivate-spin 0.7s linear infinite;
}
@keyframes deactivate-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
